<template>
  <div>
    <!-- MAIN (NOT collected yet) -->
    <v-container v-if="!collected" fluid class="donate-page pa-0">
      <!-- Progress Dialog -->
      <v-dialog v-model="dialogProgress" persistent width="320">
        <v-card class="rounded-xl" color="primary" dark>
          <v-card-text class="pa-5">
            <div class="font-weight-bold mb-2">Processing...</div>
            <div class="text-body-2 mb-3">Please stand by</div>
            <v-progress-linear indeterminate color="white" />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-row justify="center" no-gutters>
        <v-col cols="12" sm="10" md="7" lg="5" class="pa-4">
          <!-- Header -->
          <v-card v-if="Campaigns" class="card-soft mb-3" outlined>
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <div class="min-w-0">
                  <div class="text-subtitle-1 font-weight-bold text-truncate">
                    {{ Campaigns.name || "Sponsor" }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">
                    Select sponsorships, then pay securely
                  </div>
                </div>
                <v-spacer />
                <v-chip color="green lighten-4" class="font-weight-bold" label>
                  <v-icon left small>mdi-lock</v-icon>
                  Secure
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- Step Pills -->
          <div class="step-pills mb-3">
            <div :class="['pill', step >= 1 ? 'active' : '']">1 Sponsor</div>
            <div class="pill-divider"></div>
            <div :class="['pill', step >= 2 ? 'active' : '']">2 Info</div>
            <div class="pill-divider"></div>
            <div :class="['pill', step >= 3 ? 'active' : '']">3 Pay</div>
          </div>

          <!-- MAIN CARD -->
          <v-card class="card-soft" outlined>
            <!-- STEP 1: SPONSOR PICK -->
            <div v-show="step === 1" class="pa-4">
              <div class="step-wrap">
                <v-card
                  v-for="(group, index) in groups"
                  :key="index"
                  class="group-card mb-3"
                  outlined
                >
                  <div class="group-head">
                    <div class="group-title">{{ group.category }}</div>

                    <v-chip
                      small
                      class="ml-2"
                      v-if="
                        group.summary && group.summary.remainingQty !== null
                      "
                      :color="
                        group.summary.remainingQty === 0 ? 'grey' : 'green'
                      "
                      text-color="white"
                    >
                      {{ group.summary.remainingQty }} left
                    </v-chip>

                    <v-chip small class="ml-2" v-else>Available</v-chip>
                  </div>

                  <div class="items">
                    <div
                      v-for="(item, i) in group.items"
                      :key="i"
                      class="item-row"
                      :class="{ soldout: item.remainingQty === 0 }"
                    >
                      <div class="item-main">
                        <div class="item-title-row">
                          <div class="item-name">{{ item.name }}</div>

                          <v-chip
                            x-small
                            class="item-chip"
                            color="grey"
                            text-color="white"
                            v-if="item.remainingQty === 0"
                          >
                            Sold out
                          </v-chip>

                          <v-chip
                            x-small
                            class="item-chip"
                            :color="seatChipColor(item)"
                            text-color="white"
                            v-else-if="item.remainingQty !== null"
                          >
                            {{ item.remainingQty }} left
                          </v-chip>

                          <v-chip
                            x-small
                            class="item-chip"
                            color="blue-grey"
                            text-color="white"
                            v-else
                          >
                            Unlimited
                          </v-chip>
                        </div>

                        <!-- ✅ add this -->
                        <div class="item-sub" v-if="Number(item.amount) > 0">
                          <span class="item-price">{{
                            money(displayMonthlyAmount(item))
                          }}</span>
                          <span class="item-unit">/ month</span>
                        </div>
                      </div>

                      <div class="qty-ctrl">
                        <v-btn
                          icon
                          small
                          @click="minus(item)"
                          :disabled="item.qty === 0"
                        >
                          <v-icon>mdi-minus</v-icon>
                        </v-btn>

                        <div class="qty">{{ item.qty }}</div>

                        <v-btn
                          icon
                          small
                          @click="plus(item)"
                          :disabled="
                            item.remainingQty !== null &&
                            item.qty >= item.remainingQty
                          "
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-card>
              </div>

              <div class="d-flex align-center mt-2">
                <v-btn text @click="$router.go(-1)">Back</v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  rounded
                  class="px-6"
                  :disabled="!allowStep2"
                  @click="infoStep"
                >
                  Next
                </v-btn>
              </div>
            </div>

            <!-- STEP 2: INFO -->
            <div v-show="step === 2" class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-1">
                Your information
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                We’ll email your receipt.
              </div>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    autofocus
                    v-model="Donor.name"
                    :error-messages="nameErrors"
                    :hide-details="nameErrors.length === 0"
                    outlined
                    dense
                    label="Name"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="Donor.phoneNumber"
                    :error-messages="phoneNumberErrors"
                    :hide-details="phoneNumberErrors.length === 0"
                    outlined
                    dense
                    type="tel"
                    inputmode="tel"
                    label="Phone Number"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="Donor.email"
                    :error-messages="emailErrors"
                    :hide-details="emailErrors.length === 0"
                    outlined
                    dense
                    type="email"
                    inputmode="email"
                    label="Email Address"
                  />
                </v-col>
              </v-row>

              <div class="d-flex align-center mt-2">
                <v-btn text @click="step = 1">Back</v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  rounded
                  class="px-6"
                  @click="paymentInfo"
                >
                  Next
                </v-btn>
              </div>
            </div>

            <!-- STEP 3: PAYMENT -->
            <div v-show="step === 3" class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-1">Payment</div>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                {{
                  isUnitCampaign
                    ? "Enter your card details."
                    : "Choose cycle and enter your card details."
                }}
              </div>

              <v-col cols="12" class="py-2 px-0" v-if="!isUnitCampaign">
                <div class="mb-2">Payment Cycle?</div>
                <v-btn-toggle
                  @change="reCalculateFee"
                  dense
                  v-model="donationObj.paymentCycle"
                  color="primary"
                >
                  <v-btn outlined small value="monthly">Monthly</v-btn>
                  <v-btn outlined small value="oneTime">One Time</v-btn>
                </v-btn-toggle>
              </v-col>

              <v-col cols="12" class="py-2 px-0" v-else>
                <div class="mb-2">Payment Cycle</div>
                <v-chip
                  small
                  color="green lighten-4"
                  class="font-weight-bold"
                  label
                >
                  Monthly (until canceled)
                </v-chip>
              </v-col>

              <!-- ✅ unit monthly forever message -->
              <v-col v-if="isUnitCampaign" cols="12" class="px-0 text-body-2">
                Today we will charge
                <span class="font-weight-bold">{{
                  totalDonationAmount | currency
                }}</span
                >. Then we will charge the same amount on the
                <span class="font-weight-bold">10th</span>
                of each month until you cancel.

                <span v-if="businessPhone">
                  To cancel, please call
                  <span class="font-weight-bold">{{
                    businessPhone | USPhoneNumber
                  }}</span
                  >.
                </span>
              </v-col>

              <!-- existing monthly (yearly sponsor) -->
              <v-col
                v-else-if="donationObj.paymentCycle === 'monthly'"
                cols="12"
                class="px-0 text-body-2"
              >
                Today we will charge
                <span class="font-weight-bold">{{
                  totalDonationAmount | currency
                }}</span
                >. Then we will charge
                <span class="font-weight-bold">{{
                  totalDonationAmount | currency
                }}</span>
                on the <span class="font-weight-bold">10th</span> of each month
                for the next <span class="font-weight-bold">11</span> months.
              </v-col>

              <!-- existing one-time (yearly sponsor) -->
              <v-col v-else cols="12" class="px-0 text-body-2">
                We will collect a payment of
                <span class="font-weight-bold">{{
                  totalDonationAmount | currency
                }}</span>
                on
                <span class="font-weight-bold">{{
                  startingDate | moment("LL")
                }}</span>
                for the full year of sponsorship.
              </v-col>

              <CreditCard
                v-if="step === 3"
                @submit="infoCollect"
                :localCollectOnly="true"
                :donor="Donor"
                :public="true"
                :donationCategoryId="Business.DefaultDonationId"
              />

              <div class="text-body-2 mt-4" v-if="Business">
                By clicking “Pay”, you authorize an electronic fund transfer to
                {{ Business.name }}.
              </div>

              <div class="d-flex align-center mt-4">
                <v-btn text @click="step = 2">Back</v-btn>
                <v-spacer />
                <v-btn
                  color="success"
                  rounded
                  class="px-7"
                  :disabled="loading"
                  :loading="loading"
                  @click="startPay"
                >
                  Pay {{ totalDonationAmount | currency }}
                </v-btn>
              </div>
            </div>
          </v-card>

          <!-- Sticky bottom bar (mobile) -->
          <div class="sticky-bar">
            <div class="sticky-inner">
              <div class="min-w-0">
                <div class="text-caption grey--text text--darken-1">Total</div>
                <div class="font-weight-bold">
                  {{
                    step === 1 ? money(allRowTotal) : money(totalDonationAmount)
                  }}
                </div>

                <span v-if="step === 1 && monthlyRowTotal">
                  {{ money(monthlyRowTotal) }} / month
                </span>
              </div>

              <v-spacer />

              <v-btn
                v-if="step === 1"
                color="primary"
                rounded
                class="px-6"
                :disabled="!allowStep2"
                @click="infoStep"
              >
                Next
              </v-btn>

              <v-btn
                v-else-if="step === 2"
                color="primary"
                rounded
                class="px-6"
                @click="paymentInfo"
              >
                Next
              </v-btn>

              <v-btn
                v-else
                color="success"
                rounded
                class="px-6"
                :disabled="loading"
                :loading="loading"
                @click="startPay"
              >
                Pay
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- SUCCESS -->
    <v-container v-else :fluid="$vuetify.breakpoint.xsOnly" class="page-wrap">
      <v-card flat>
        <v-card-text>
          <lottie
            :height="200"
            :options="defaultOptions"
            name="receiptCheck"
            v-on:animCreated="handleAnimation"
            @onComplete="showHomeButton = true"
          />
          <div class="text-center">
            <div class="text-h5">All Set</div>
            <div class="mt-3">
              The Prophet, upon be peace, said: “The believer’s shade on the Day
              of Resurrection will be his charity.”
              <span class="font-weight-bold">Al-Tirmidhi</span>
            </div>
          </div>
          <div class="text-center mt-5" v-if="showHomeButton">
            <v-slide-y-transition>
              <v-btn block large @click="$router.go(-1)" color="success">
                Home
              </v-btn>
            </v-slide-y-transition>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import donationService from "@/modules/Campaign/service.js";
import CreditCard from "@/modules/PaymentMethod/components/CreditCard.vue";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/1708-success.json";

const DONOR_STORAGE_KEY = "taleem_public_donor_v1";
const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

const pjson = require("../../../../../package.json");

export default {
  name: "collectDonationBySponsor",
  data() {
    return {
      dialogProgress: false,
      donationLimit: false,
      paidInFullAmount: 0,
      donorSaveTimer: null,
      allRowTotal: 0,
      monthlyRowTotal: 0,
      donationSelectedAmount: 0,
      selectedRow: null,
      Donor: {
        name: null,
        phoneNumber: null,
        email: null,
      },
      dateFormatted: null,
      startingDate: this.$moment(),
      minDate: null,
      donationObj: {
        paymentType: "card",
        paymentCycle: "monthly",
        accountType: "personal",
        startingDate: this.$moment(),
      },
      groups: [],
      collected: false,
      card: null,
      showHomeButton: false,
      showSecondCollection: true,
      loading: false,
      feeAmount: 0,
      allowStep2: false,
      totalDonationAmount: 0,
      step: 1,
      selectedAmount: 0,
      amount: 10,
      paymentCycle: "monthly",
      Campaigns: null,
      Business: null,
      addFee: false,
      addFeeSecond: false,
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: true,
      },
    };
  },
  created() {
    this.loadDonorFromLocal();
    this.loadCampaign();
  },
  mixins: [validationMixin],
  validations: {
    Donor: {
      name: { required },
      phoneNumber: { required },
      email: { required, email },
    },
  },
  components: { CreditCard, Lottie },
  computed: {
    isUnitCampaign() {
      const md = this.Campaigns?.metadata || {};
      return (md.detailStyle || "").toString().toLowerCase() === "units";
    },
    durationType() {
      return (this.Campaigns?.metadata?.durationType || "yearly")
        .toString()
        .toLowerCase()
        .trim();
    },
    isYearlyDuration() {
      return !this.isUnitCampaign && this.durationType === "yearly";
    },

    // optional helper for phone display
    businessPhone() {
      return this.Business?.phoneNumber || this.Business?.phone || "";
    },
    nextChargeDate() {
      const now = this.$moment();
      const tenth = now.clone().date(10).startOf("day");
      return now.date() <= 10 ? tenth : tenth.add(1, "month");
    },
    version() {
      return pjson.version;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.Donor.name.$dirty) return errors;
      if (!this.$v.Donor.name.required) errors.push("First Name is required");
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.Donor.phoneNumber.$dirty) return errors;
      if (!this.$v.Donor.phoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.Donor.email.$dirty) return errors;
      if (!this.$v.Donor.email.required)
        errors.push("Email Address is required");
      if (!this.$v.Donor.email.email) errors.push("Email is not valid");
      return errors;
    },
  },
  watch: {
    Donor: {
      deep: true,
      handler() {
        clearTimeout(this.donorSaveTimer);
        this.donorSaveTimer = setTimeout(() => {
          this.saveDonorToLocal();
        }, 250);
      },
    },
    allRowTotal(val) {
      this.allowStep2 = val > 0;
    },
  },
  methods: {
    startPay() {
      // step 3 only
      if (this.step !== 3) return;

      // Validate donor first (optional but good)
      this.$v.Donor.$touch();
      if (this.$v.Donor.$invalid) return;

      // Reset previous card so we don’t accidentally reuse old token
      this.card = null;

      // Show loading NOW so user sees something happening
      this.loadingSubmit = true;
      this.loading = true;
      this.dialogProgress = true;

      // Ask CreditCard component to tokenize/collect card
      this.$events.emit("submitCreditCard");
    },

    // CreditCard emits @submit="infoCollect"
    infoCollect(val) {
      this.card = val;
      // Now we have the card, finalize payment
      this.finalizePay();
    },

    finalizePay() {
      try {
        const data = {};
        data.Donor = this.Donor;

        // REQUIRED: pass card to backend
        data.card = this.card;
        if (!data.card) throw new Error("Card was not collected");

        // REQUIRED: campaign info
        data.campaignType = this.Campaigns?.type;
        data.CampaignId = this.Campaigns?.id;

        // cycle
        if (this.isUnitCampaign) this.donationObj.paymentCycle = "monthly";
        data.cycle = this.donationObj.paymentCycle;

        // build selected rows (you commented this out, backend needs it)
        data.selectedRow = [];
        this.groups.forEach((categoryRow) => {
          categoryRow.items.forEach((row) => {
            if (row.qty > 0) {
              data.selectedRow.push({
                ...row,
                fullName: row.name.concat(" ", categoryRow.category),
              });
            }
          });
        });

        // amount math (base charge)
        const monthlyTotal = Number(this.monthlyRowTotal || 0); // always monthly
        const baseAmount = this.isUnitCampaign
          ? monthlyTotal
          : data.cycle === "monthly"
          ? monthlyTotal
          : monthlyTotal * 12;

        data.monthlyAmount = monthlyTotal;
        data.amount = baseAmount;

        // fee
        data.feeAmount = Number(this.feeAmount || 0);
        data.processingFeeCollect = !!(this.addFee || this.addFeeSecond);
        data.finalAmount = data.processingFeeCollect
          ? Number(data.amount) + Number(data.feeAmount)
          : Number(data.amount);

        donationService
          .publicDonationSponsor(this.Business.code, data)
          .then(() => {
            this.loadingSubmit = false;
            this.collected = true;
            this.loading = false;
            this.dialogProgress = false;
          })
          .catch((error) => {
            this.loadingSubmit = false;
            this.loading = false;
            this.dialogProgress = false;

            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: error?.data?.message || "Payment failed",
              });
          });
      } catch (e) {
        this.loadingSubmit = false;
        this.loading = false;
        this.dialogProgress = false;
        console.log("finalizePay error", e);
      }
    },
    displayMonthlyAmount(row) {
      const amt = Number(row?.amount || 0);

      if (this.isUnitCampaign) return amt; // units are always monthly already
      if (this.isYearlyDuration) return amt / 12; // yearly => show monthly
      return amt; // monthly => already monthly
    },
    seatChipColor(item) {
      const left = Number(item?.remainingQty ?? 0);
      if (left === 0) return "grey";
      if (left <= 3) return "red";
      if (left <= 10) return "orange";
      return "green";
    },
    money(val) {
      const n = Number(val || 0);
      return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
    saveDonorToLocal() {
      try {
        const payload = {
          name: (this.Donor?.name || "").trim(),
          phoneNumber: (this.Donor?.phoneNumber || "").toString().trim(),
          email: (this.Donor?.email || "").trim(),
          updatedAt: new Date().toISOString(),
        };
        if (!payload.name && !payload.phoneNumber && !payload.email) return;
        localStorage.setItem(DONOR_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        // ignore
      }
    },

    loadDonorFromLocal() {
      try {
        const raw = localStorage.getItem(DONOR_STORAGE_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw) || {};
        if (!this.Donor.name && saved.name) this.Donor.name = saved.name;
        if (!this.Donor.phoneNumber && saved.phoneNumber)
          this.Donor.phoneNumber = saved.phoneNumber;
        if (!this.Donor.email && saved.email) this.Donor.email = saved.email;
      } catch (e) {
        // ignore
      }
    },

    reCalculateFee() {
      const monthlyTotal = Number(this.monthlyRowTotal || 0);
      const base = this.isUnitCampaign
        ? monthlyTotal
        : this.donationObj.paymentCycle === "monthly"
        ? monthlyTotal
        : monthlyTotal * 12;

      this.feeAmount = ((base * 3.4) / 100 + 0.3).toFixed(2);

      const fee = Number(this.feeAmount || 0);
      this.totalDonationAmount =
        this.addFee || this.addFeeSecond ? base + fee : base;
    },

    getFormattedDay(date) {
      const dayOfMonth = this.$moment(date).date();
      const suffix = this.getDaySuffix(dayOfMonth);
      return dayOfMonth + suffix;
    },

    getDaySuffix(day) {
      if (day >= 11 && day <= 13) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    },

    plus(item) {
      if (item.remainingQty !== null && item.qty >= item.remainingQty) return;
      item.qty += 1;
      this.setDonationAmount();
    },

    minus(item) {
      if (item.qty <= 0) return;
      item.qty -= 1;
      this.setDonationAmount();
    },

    handleAnimation(anim) {
      this.anim = anim;
    },

    groupByCategory(data) {
      const temp = {};

      (data.CampaignDetails || []).forEach((d) => {
        const category = d.category || "Other";
        if (!temp[category]) temp[category] = [];

        temp[category].push({
          id: d.id,
          name: d.name,
          amount: Number(d.amount ?? d.price ?? 0) || 0,
          qty: 0,
          capacity: Number(d.capacity) || 0,
          soldQty: Number(d.soldQty) || 0,
          remainingQty:
            d.remainingQty === null ? null : Number(d.remainingQty) || 0,
        });
      });

      return Object.keys(temp).map((category) => ({
        category,
        summary: data.categorySummary?.[category] || null,
        items: temp[category],
      }));
    },

    submit() {
      try {
        const data = {};
        data.Donor = this.Donor;

        this.$events.emit("submitCreditCard");

        data.donationLimit = this.donationLimit;

        if (this.isUnitCampaign) this.donationObj.paymentCycle = "monthly";

        data.cycle = this.donationObj.paymentCycle;

        // const monthlyTotal = Number(this.allRowTotal || 0);
        // const baseAmount = this.isUnitCampaign
        //   ? monthlyTotal
        //   : data.cycle === "monthly"
        //   ? monthlyTotal
        //   : monthlyTotal * 12;

        // data.amount = baseAmount; // ✅ what you actually charge (before fee)
        // data.monthlyAmount = monthlyTotal; // optiona

        // data.card = this.card;
        // data.campaignType = this.Campaigns.type;
        // data.CampaignId = this.Campaigns.id;

        // data.selectedRow = [];
        // this.groups.forEach((cateogryRow) => {
        //   cateogryRow.items.forEach((row) => {
        //     if (row.qty > 0) {
        //       row.fullName = row.name.concat(" ", cateogryRow.category);
        //       data.selectedRow.push(row);
        //     }
        //   });
        // });

        // data.amount = this.allRowTotal;
        // data.feeAmount = Number(this.feeAmount || 0);
        const monthlyTotal = Number(this.allRowTotal || 0);

        const baseAmount = this.isUnitCampaign
          ? monthlyTotal
          : data.cycle === "monthly"
          ? monthlyTotal
          : monthlyTotal * 12;

        // send both so backend can use what it needs
        data.monthlyAmount = monthlyTotal;
        data.amount = baseAmount; // DO NOT overwrite later

        data.feeAmount = Number(this.feeAmount || 0);
        data.processingFeeCollect = !!(this.addFee || this.addFeeSecond);
        data.finalAmount = data.processingFeeCollect
          ? data.amount + data.feeAmount
          : data.amount;

        data.processingFeeCollect = !!(this.addFee || this.addFeeSecond);
        data.finalAmount = data.processingFeeCollect
          ? parseFloat(data.amount) + parseFloat(data.feeAmount)
          : parseFloat(data.amount);

        if (!data.card) return false;

        this.loadingSubmit = true;
        this.loading = true;
        this.dialogProgress = true;

        donationService
          .publicDonationSponsor(this.Business.code, data)
          .then(() => {
            this.loadingSubmit = false;
            this.collected = true;
            this.loading = false;
          })
          .catch((error) => {
            this.loadingSubmit = false;
            this.loading = false;
            this.dialogProgress = false;

            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: error?.data?.message || "Payment failed",
              });
          });
      } catch (error) {
        this.dialogProgress = false;
        this.loadingSubmit = false;
        this.loading = false;
      }
    },

    paymentInfo() {
      this.$v.Donor.$touch();
      if (this.$v.Donor.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.saveDonorToLocal();
        this.step = 3;
      }
    },
    setDonationAmount() {
      if (this.step === 1 && this.groups.length) {
        let yearly = 0;
        let monthly = 0;

        this.groups.forEach((category) => {
          category.items.forEach((row) => {
            const qty = Number(row.qty || 0);
            const raw = Number(row.amount || 0); // raw stored amount
            if (qty <= 0) return;

            yearly += raw * qty;
            monthly += this.displayMonthlyAmount(row) * qty;
          });
        });

        // ✅ Total shown in UI:
        // - yearly campaigns: show yearly total
        // - monthly + units: show monthly total
        this.allRowTotal = this.isYearlyDuration ? yearly : monthly;

        // ✅ always keep monthly for sticky "/ month"
        this.monthlyRowTotal = monthly;
      }

      this.feeAmount = 0;
      this.reCalculateFee();
    },

    infoStep() {
      this.step = 2;
    },

    loadCampaign() {
      const code = this.$route.params.id;

      donationService
        .getCampaignById(code)
        .then((response) => {
          this.Campaigns = response.data;
          this.Business = response.data.Business;

          if (this.Campaigns?.metadata?.preDefinedValue?.length) {
            this.preDefinedValue = this.Campaigns.metadata.preDefinedValue;
            this.amount = this.preDefinedValue[0];
          }

          if (this.Campaigns?.metadata?.donationLimit) {
            this.donationLimit = true;
          }

          this.groups = this.groupByCategory(this.Campaigns);
          if (this.isUnitCampaign) {
            this.donationObj.paymentCycle = "monthly";
            this.reCalculateFee();
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.donate-page {
  background: #f6f7f9;
  min-height: 100vh;
}

.page-wrap {
  padding: 16px;
  padding-bottom: 110px;
}

.card-soft {
  border-radius: 18px;
  overflow: hidden;
}

.rounded-xl {
  border-radius: 18px !important;
}

.min-w-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Step pills */
.step-pills {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pill {
  flex: 1;
  text-align: center;
  padding: 10px 10px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  font-weight: 800;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
}

.pill.active {
  background: #e8f0ff;
  border-color: rgba(25, 118, 210, 0.25);
  color: rgba(0, 0, 0, 0.8);
}

.pill-divider {
  width: 10px;
  height: 2px;
  background: rgba(0, 0, 0, 0.12);
  border-radius: 999px;
}

.step-wrap {
  padding-bottom: 16px;
}

.group-card {
  border-radius: 16px;
  overflow: hidden;
}

.group-head {
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 800;
}

.group-title {
  font-size: 14px;
}

.items {
  padding: 6px 10px 10px;
}

.item-row {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  padding: 12px 10px;
  border-radius: 14px;
}

.item-row + .item-row {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.item-main {
  min-width: 0;
  flex: 1;
}

.item-name {
  font-weight: 800;
  font-size: 14px;
  line-height: 1.2;
}

.item-meta {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.8;
  display: flex;
  gap: 6px;
  align-items: center;
}

.price {
  font-weight: 800;
}

.dot {
  opacity: 0.6;
}

.qty-ctrl {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
  padding: 4px 6px;
}

.qty {
  width: 26px;
  text-align: center;
  font-weight: 800;
}

.soldout {
  opacity: 0.55;
}

/* Sticky bar */
.sticky-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  z-index: 50;
}

.sticky-inner {
  max-width: 760px;
  margin: 0 auto;
  background: white;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.item-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0; /* important so text truncation works */
}

.item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap; /* keeps the name on one line */
}

.item-chip {
  flex: 0 0 auto;
  margin: 0 !important; /* remove the ml-2 behavior */
  white-space: nowrap;
}
.item-sub {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.75;
  display: flex;
  gap: 4px;
  align-items: center;
}

.item-price {
  font-weight: 800;
}

.item-unit {
  font-weight: 600;
}
</style>